import IConfigurationModel, { ConfigurationModel } from '@/models/configuration-model'
import * as UrlConstants from '@/constants/url-constants'
import * as StorageConstants from '@/constants/storage-constants'
import bodySettings from '@/settings/body-settings.json'
import edgeSettings from '@/settings/edge-settings.json'
import frontSettings from '@/settings/front-settings.json'
import insideSettings from '@/settings/inside-settings.json'
import pocketSettings from '@/settings/pocket-settings.json'
import sideSettings from '@/settings/side-settings.json'
import tackSettings from '@/settings/tack-settings.json'
import backSettings from '@/settings/back-settings.json'
import threadSettings from '@/settings/thread-settings.json'
import bagsizeSettings from '@/settings/bagsize-settings.json'

type AllowedMap = Record<string, Set<string>>;

function idsFromArray(arr: any[]): string[] {
  return (arr ?? []).map((x) => String(x.id)).filter(Boolean)
}

export const allowedIds: AllowedMap = {
  bagsize: new Set(idsFromArray((bagsizeSettings as any).bagsizeOptions)),
  body: new Set(idsFromArray((bodySettings as any).fullorderColors)),
  edge: new Set(idsFromArray((edgeSettings as any).edgeColors)),
  front: new Set(idsFromArray((frontSettings as any).futaShapeTextures)),
  inside: new Set(idsFromArray((insideSettings as any).futaNoUraTextures)),
  pocket: new Set(idsFromArray((pocketSettings as any).pocketTextures)),
  side: new Set(idsFromArray((sideSettings as any).sideTextures)),
  tack: new Set(idsFromArray((tackSettings as any).options)),
  back: new Set(idsFromArray((backSettings as any).backColors)),
  thread: new Set(idsFromArray((threadSettings as any).threadColors))
}

export default class InitConfigurationService {
  determine(): IConfigurationModel {
    const urlParams = new URLSearchParams(window.location.search)

    let model: IConfigurationModel
    if (urlParams.has(UrlConstants.bagsize) || urlParams.has(UrlConstants.body)) {
      model = this.determineUrlModel(urlParams)
    } else if (this.hasLocalConfig()) {
      model = this.determineLocalstorageModel()
    } else {
      model = new ConfigurationModel()
    }

    return this.sanitize(model)
  }

  private sanitize(model: IConfigurationModel): IConfigurationModel {
    const m = model as any

    const sanitizeId = (field: string) => {
      const v = m[field]
      if (v == null) return
      const s = String(v)
      const set = allowedIds[field]
      if (set && !set.has(s)) {
        m[field] = null
        this.safeRemoveFromStorage(field)
      } else {
        m[field] = s
      }
    }

    sanitizeId('bagsize')
    sanitizeId('body')
    sanitizeId('edge')
    sanitizeId('thread')
    sanitizeId('inside')
    sanitizeId('pocket')
    sanitizeId('side')
    sanitizeId('front')
    sanitizeId('tack')
    sanitizeId('back')
    sanitizeId('namechar')
    sanitizeId('noName')

    if (m.namethread != null && String(m.namethread) === 'null') m.namethread = ''

    return model
  }

  private safeRemoveFromStorage(field: string) {
    const map: Record<string, string> = {
      bagsize: StorageConstants.bagsize,
      body: StorageConstants.body,
      edge: StorageConstants.edge,
      thread: StorageConstants.thread,
      inside: StorageConstants.inside,
      pocket: StorageConstants.pocket,
      side: StorageConstants.side,
      front: StorageConstants.front,
      tack: StorageConstants.tack,
      back: StorageConstants.back,
      namechar: StorageConstants.namechar,
      namethread: StorageConstants.namethread,
      noName: StorageConstants.noName
    }

    try {
      const key = map[field]
      if (key) localStorage.removeItem(key)
    } catch {
      console.warn(`Failed to remove invalid localStorage item for field ${field}`)
    }
  }

  private hasLocalConfig(): boolean {
    try {
      const keys = [
        StorageConstants.bagsize,
        StorageConstants.body,
        StorageConstants.edge,
        StorageConstants.thread,
        StorageConstants.noName,
        StorageConstants.side
      ]
      return keys.some((k) => this.readStoredString(k) != null)
    } catch (err) {
      console.error(err)
      return false
    }
  }

  private determineUrlModel(urlParams: URLSearchParams): IConfigurationModel {
    const model = new ConfigurationModel()
    model.bagsize = this.formatId(urlParams.get(UrlConstants.bagsize))
    model.body = this.formatId(urlParams.get(UrlConstants.body))
    model.edge = this.formatId(urlParams.get(UrlConstants.edge))
    model.thread = this.formatId(urlParams.get(UrlConstants.thread))
    model.inside = this.formatId(urlParams.get(UrlConstants.inside))
    model.pocket = this.formatId(urlParams.get(UrlConstants.pocket))
    model.side = this.formatId(urlParams.get(UrlConstants.side))
    model.front = this.formatId(urlParams.get(UrlConstants.front))
    model.tack = this.formatId(urlParams.get(UrlConstants.tack))
    model.back = this.formatId(urlParams.get(UrlConstants.back))
    model.namechar = this.formatId(urlParams.get(UrlConstants.namechar))
    model.namethread = this.formatString(urlParams.get(UrlConstants.namethread))
    model.noName = this.formatId(urlParams.get(UrlConstants.noName))
    return model
  }

  private determineLocalstorageModel(): IConfigurationModel {
    const model = new ConfigurationModel()
    model.bagsize = this.readStoredString(StorageConstants.bagsize)
    model.body = this.readStoredString(StorageConstants.body)
    model.edge = this.readStoredString(StorageConstants.edge)
    model.thread = this.readStoredString(StorageConstants.thread)
    model.inside = this.readStoredString(StorageConstants.inside)
    model.pocket = this.readStoredString(StorageConstants.pocket)
    model.side = this.readStoredString(StorageConstants.side)
    model.front = this.readStoredString(StorageConstants.front)
    model.tack = this.readStoredString(StorageConstants.tack)
    model.back = this.readStoredString(StorageConstants.back)
    model.namechar = this.readStoredString(StorageConstants.namechar)
    model.namethread = this.formatString(this.readStoredString(StorageConstants.namethread))
    model.noName = this.readStoredString(StorageConstants.noName)
    return model
  }

  private readStoredString(key: string): string | null {
    try {
      const raw = localStorage.getItem(key)
      return this.formatId(raw)
    } catch {
      return null
    }
  }

  private formatId(value: string | null): string | null {
    if (value == null) return null
    if (value.length <= 0) return null
    if (value === 'null') return null

    try {
      const parsed = JSON.parse(value)
      if (parsed == null) return null
      const s = String(parsed)
      if (s === 'null' || s.length <= 0) return null
      return s
    } catch {
      return value
    }
  }

  private formatString(value: string | null): string {
    if (value == null || value.length <= 0 || value === 'null') return ''
    return value
  }
}